import React, {useContext, useEffect, useMemo, useState, useRef} from "react";
import deepmerge from 'deepmerge';
import { actions } from "../actions/error";
import { CONFIG } from "../config";
import { Routes } from '../config/Routes';
import { useFetch } from "../services/fetch";
import { useCognitoProvider } from "./CognitoContext";
import { useErrorProvider } from "./ErrorProvider";
import { overwriteMerge } from "../utils/functions";
import { errorMessageQuery } from "../utils/variables";

const MenuContext = React.createContext({});

/**
 * Provider para gestionar los elementos que conforman el menú.
 * @param {Object} props - Propiedades del componente.
 * @param {React.ReactChildren} props.children - Componentes hijo.
 * @returns {React.Component}
 */
export default function MenuProvider({ children }) {

    const { state } = useCognitoProvider();
    const {interceptedFetch} = useFetch();
    const { dispatch: errorDispatch } = useErrorProvider();
    const [routes, setRoutes] = useState([...Routes]);
    const [currentRoute, setCurrentRoute] = useState(routes[0]);
    const [loading, setLoading] = useState(true);
    const subMenusToRemove = useRef([]);
    const contextValue = useMemo(() => ({ routes, setRoutes, currentRoute, setCurrentRoute, loading, setLoading }), [routes, currentRoute, loading]);

    useEffect(() => { handleSubMenuVisibility(); }, [state]);

    const handleSubMenuVisibility = () => {   
        if(!state.pkOperador) return;     
        let promises = [];
        let endpoints = [];
        routes.forEach((route, index) => {   
            if(route.dynamicSubMenu) {
                setLoading(true);   
                const endpoint = route.endpoint;
                const subMenus = route.submenu;
                if(subMenus) {
                    subMenus.forEach((subMenu, subMenuIndex) => {
                        subMenusToRemove.current.push({ parent: index, submenu: subMenuIndex });                        
                        const endpointAlreadyQueued = endpoints.find(f => f === endpoint);
                        if(!endpointAlreadyQueued) {
                            promises.push(allocRequests(endpoint));
                            endpoints.push(endpoint);
                        }
                    })   
                }   
            }   
        });  
        fetchAllRequests(promises);
    }

    const allocRequests = async endpoint => {
        const apiData = CONFIG.api.find(f => f.key === endpoint);
        return interceptedFetch(
            `${apiData.endpoint}/${state.pkOperador}`, 
            { method: apiData.method, headers: { 'Authorization': state.idToken, 'Content-Type': 'application/json' } }
        ); 
    }

    const fetchAllRequests = async promises => {
        try {
            const results = await Promise.all(promises).then( async (res) => Promise.all( res.map( async (data) => data.json() ) ) );
            applySubMenuVisibility(results);
        } catch (e) { 
            errorDispatch({type: actions.OPEN_MODAL, payload: errorMessageQuery});
        } finally { setLoading(false); }
    }

    const applySubMenuVisibility = results => {
        // guardamos en un buffer los elementos del submenu, para que al quitar elementos, porque no tengan datos en alguno, no se quiten del submenu original.
        routes.forEach(route => route['submenuBuffer'] = deepmerge([], route.submenu, { arrayMerge: overwriteMerge}))
        const newRoutes = [...routes];
        subMenusToRemove.current.forEach(subMenuToRemove => {
            const newRoute = newRoutes[subMenuToRemove.parent];            
            newRoute.submenuBuffer = newRoute.submenuBuffer.map(subMenu => {
                const result = results[0][subMenu.dataKey];
                return result && result.length > 0 ? subMenu : null
            }).filter(f => f);
        })
    }

    return <MenuContext.Provider value={contextValue}>
        {children}
    </MenuContext.Provider>
}

/**
 * Hook para utilizar este provider en el resto de componentes.
 */
export const useMenuProvider = () => useContext(MenuContext);